import React, { useEffect, useMemo, useState } from 'react';
import {
	Autocomplete,
	CircularProgress,
	IconButton,
	InputAdornment,
	TextField,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { useSelector } from 'react-redux';
import { Configure, InstantSearch, useHits, useSearchBox } from 'react-instantsearch';
import { useGetUserDetailsQuery } from '@/features/user/userApi';

const AutocompleteInput = ({
	query,
	refine,
	clear,
	hits,
	isSearching,
	selectedName,
	onSelect,
	onClear,
	disabled,
	error,
	helperText,
	required,
	labels,
	mapOption,
}) => {
	const [open, setOpen] = useState(false);
	const options = useMemo(() => (query.length > 0 ? hits : []), [query, hits]);

	useEffect(() => {
		refine(selectedName);
	}, [selectedName]);

	const handleSearch = () => {
		if (query.length > 0) {
			refine(query);
		}
	};

	const handleReset = () => {
		setOpen(false);
		clear();
		onClear();
	};

	return (
		<Autocomplete
			id={labels.inputId}
			fullWidth
			loading={isSearching}
			loadingText={labels.loadingText}
			options={options}
			open={open}
			disabled={Boolean(disabled)}
			noOptionsText={query.length > 0 ? labels.emptyText : 'Type to search'}
			filterOptions={(x) => x}
			inputValue={query}
			onOpen={() => setOpen(true)}
			onClose={() => setOpen(false)}
			getOptionLabel={mapOption.label}
			onInputChange={(_, value, reason) => {
				if (reason !== 'reset') {
					refine(value);
				}
			}}
			renderOption={(props, option) => (
				<li {...props} key={option.id}>
					{mapOption.optionText(option)}
				</li>
			)}
			renderInput={(params) => (
				<TextField
					{...params}
					required={required}
					error={Boolean(error)}
					helperText={helperText}
					placeholder={labels.placeholder}
					onKeyDown={(event) => {
						if (event.key === 'Enter') {
							event.preventDefault();
							handleSearch();
						}
					}}
					InputProps={{
						...params.InputProps,
						startAdornment: (
							<InputAdornment position='start'>
								<IconButton
									disabled={Boolean(isSearching || disabled)}
									sx={{ padding: '0' }}
									onClick={handleSearch}
								>
									<SearchIcon />
								</IconButton>
							</InputAdornment>
						),
						endAdornment: (
							<InputAdornment position='end'>
								{isSearching ? (
									<CircularProgress color='inherit' size={20} />
								) : null}
								{query.length > 0 && (
									<IconButton
										size='small'
										disabled={Boolean(isSearching || disabled)}
										sx={{ padding: '0' }}
										onClick={handleReset}
									>
										<ClearIcon fontSize='20px' />
									</IconButton>
								)}
							</InputAdornment>
						),
					}}
				/>
			)}
			isOptionEqualToValue={(option, selected) => option.id === selected.id}
			onChange={(_, value) => {
				if (!value) {
					handleReset();
					return;
				}

				refine(mapOption.label(value));
				onSelect(value);
			}}
		/>
	);
};

const RelationshipSelectInput = ({ mapOption, selectedName, ...props }) => {
	const { items } = useHits();
	const { query, refine, clear, isSearchStalled } = useSearchBox();

	return (
		<AutocompleteInput
			hits={items.map(mapOption.fromHit)}
			selectedName={selectedName}
			mapOption={mapOption}
			query={query}
			refine={refine}
			clear={clear}
			isSearching={isSearchStalled}
			{...props}
		/>
	);
};

export const RelationshipSelect = ({
	searchClient,
	indexName,
	labels,
	mapOption,
	extraFilter,
	value,
	onSelect,
	onClear,
	disabled = false,
	error = false,
	helperText,
	required = false,
}) => {
	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;
	const userQuery = useGetUserDetailsQuery({ email }, { skip: !email });
	const brokerGroupIds = userQuery.data?.brokerGroupArray ?? [];

	const filters = useMemo(() => {
		const brokerFilter = `brokerGroupId: [${brokerGroupIds.join(',')}]`;
		return extraFilter ? `${brokerFilter} && ${extraFilter}` : brokerFilter;
	}, [brokerGroupIds, extraFilter]);

	return (
		<InstantSearch searchClient={searchClient} indexName={indexName}>
			<Configure hitsPerPage={15} filters={filters} />
			<RelationshipSelectInput
				labels={labels}
				mapOption={mapOption}
				selectedName={mapOption.selectedLabel(value)}
				onSelect={onSelect}
				onClear={onClear}
				disabled={Boolean(disabled || userQuery.isLoading)}
				error={error}
				helperText={helperText}
				required={required}
			/>
		</InstantSearch>
	);
};
